/**
 * ProjectEditDialog — 知识库选择 Tab
 * 支持切换「我的知识库」/「共享知识库」来源；支持把模板/指南文档直接上传到所选知识库
 */
import React, { useState, useRef } from 'react';
import { KnowledgeBase } from '../types';
import { knowledgeApi, getErrorMessage } from '../services/api';

interface KnowledgeBaseSelectTabProps {
  ownedKnowledgeBases: KnowledgeBase[];
  sharedKnowledgeBases: KnowledgeBase[];
  knowledgeBaseIds: number[];
  setKnowledgeBaseIds: React.Dispatch<React.SetStateAction<number[]>>;
  onKbChanged?: () => void;
}

export default function KnowledgeBaseSelectTab({
  ownedKnowledgeBases, sharedKnowledgeBases, knowledgeBaseIds, setKnowledgeBaseIds, onKbChanged,
}: KnowledgeBaseSelectTabProps) {
  const [source, setSource] = useState<'owned' | 'shared'>('owned');
  const knowledgeBases = source === 'owned' ? ownedKnowledgeBases : sharedKnowledgeBases;

  const [uploadKbId, setUploadKbId] = useState<number | ''>('');
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleUpload = async () => {
    if (!uploadKbId) { setUploadMsg({ type: 'error', text: '请选择目标知识库' }); return; }
    if (!uploadFiles || !uploadFiles.length) { setUploadMsg({ type: 'error', text: '请选择要上传的模板文件' }); return; }
    setUploading(true); setUploadMsg(null);
    try {
      const files = Array.from(uploadFiles);
      await knowledgeApi.uploadFiles(Number(uploadKbId), files);
      setUploadMsg({ type: 'success', text: `已上传 ${files.length} 个模板到知识库` });
      setUploadFiles(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onKbChanged?.();
    } catch (e) {
      setUploadMsg({ type: 'error', text: getErrorMessage(e, '上传失败') });
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-500">
        关联知识库，生成内容时将引用所选知识库中的资料（可多选）
      </div>

      {/* 来源切换 */}
      <div className="flex gap-1">
        <button
          onClick={() => setSource('owned')}
          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
            source === 'owned'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          我的知识库
        </button>
        <button
          onClick={() => setSource('shared')}
          className={`px-3 py-1 text-xs font-medium rounded-lg transition-colors ${
            source === 'shared'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
          }`}
        >
          共享知识库
        </button>
      </div>

      {/* 上传模板到知识库 */}
      {ownedKnowledgeBases.length > 0 && (
        <div className="border border-dashed border-gray-300 rounded-lg p-3 space-y-2 bg-gray-50">
          <div className="text-sm font-medium text-gray-700">上传模板 / 指南到知识库</div>
          <select
            value={uploadKbId}
            onChange={(e) => setUploadKbId(e.target.value ? Number(e.target.value) : '')}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">选择目标知识库</option>
            {ownedKnowledgeBases.map((kb) => (
              <option key={kb.id} value={kb.id}>{kb.name}</option>
            ))}
          </select>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={(e) => setUploadFiles(e.target.files)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading}
              className="px-3 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {uploading ? '上传中...' : '上传'}
            </button>
            {uploadMsg && (
              <span className={`text-xs ${uploadMsg.type === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                {uploadMsg.text}
              </span>
            )}
          </div>
        </div>
      )}

      {knowledgeBases.length === 0 ? (
        <p className="text-xs text-gray-400 py-2">
          {source === 'owned'
            ? '暂无可用的知识库，请在"我的设置 → 知识库"中创建'
            : '暂无共享的知识库'}
        </p>
      ) : (
        <div className="max-h-64 overflow-y-auto border border-gray-300 rounded-lg divide-y divide-gray-100">
          {knowledgeBases.map((kb) => {
            const checked = knowledgeBaseIds.includes(kb.id);
            return (
              <label key={kb.id} className="flex items-center px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors">
                <input type="checkbox" checked={checked}
                  onChange={() => setKnowledgeBaseIds((prev) => checked ? prev.filter((id) => id !== kb.id) : [...prev, kb.id])}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                <span className="ml-2 text-sm text-gray-700 flex-1">{kb.name}</span>
                {source === 'shared' && (
                  <span className="text-xs text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">共享</span>
                )}
                <span className="text-xs text-gray-400 ml-2">{kb.file_count} 个文件</span>
              </label>
            );
          })}
        </div>
      )}
      {knowledgeBaseIds.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {knowledgeBases.filter((kb) => knowledgeBaseIds.includes(kb.id)).map((kb) => (
            <span key={kb.id} className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs bg-purple-50 text-purple-700">
              {kb.name}
              <button type="button" onClick={() => setKnowledgeBaseIds((prev) => prev.filter((id) => id !== kb.id))}
                className="text-purple-400 hover:text-purple-600">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
